import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Modal, Input, Select, Spin, Empty, Button, Tag, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";
import customClientAxios from "../customAxios";
import * as Api from "../utils/ApiEndpoints";

const Toolbar = styled.div`
  display: grid; grid-template-columns: 2fr 1.2fr 1fr; gap: 12px; margin-bottom: 16px;
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;
  max-height: 480px; overflow-y: auto; padding-right: 4px;
`;
const Card = styled.button`
  text-align: left; background: white; border: 2px solid ${(p) => (p.$selected ? "#6366f1" : "#e2e8f0")};
  border-radius: 12px; padding: 12px; cursor: pointer; display: flex; gap: 12px;
  transition: all 0.15s ease; font-family: inherit;
  &:hover { border-color: ${(p) => (p.$selected ? "#6366f1" : "#94a3b8")}; }
`;
const Cover = styled.div`
  width: 80px; height: 80px; border-radius: 8px; flex-shrink: 0;
  background: ${(p) => (p.$src ? `url(${p.$src}) center/cover no-repeat` : "#f1f5f9")};
`;
const Body = styled.div`flex: 1; min-width: 0;`;
const Title = styled.div`font-weight: 600; color: #0f172a; font-size: 14px; line-height: 1.3; margin-bottom: 4px;`;
const Sub = styled.div`color: #64748b; font-size: 12px; margin-bottom: 6px;`;
const FooterBar = styled.div`display: flex; align-items: center; justify-content: space-between; padding-top: 12px;`;

export default function EventPickerModal({ open, onClose, onConfirm, existingOfferingIds = [] }) {
  const [brandPages, setBrandPages] = useState([]);
  const [brandPageId, setBrandPageId] = useState(null);
  const [status, setStatus] = useState("upcoming");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    customClientAxios()
      .get(Api.CategoryPageBrandPagesFilter)
      .then((r) => setBrandPages(r.data?.data || []))
      .catch(() => {});
  }, [open]);

  const fetchOfferings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status, page: 1, limit: 60 };
      if (brandPageId) params.brandPageId = brandPageId;
      if (q) params.q = q;
      const res = await customClientAxios().get(Api.CategoryPageOfferingsSearch, { params });
      setItems(res.data?.data || []);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to load offerings");
    } finally {
      setLoading(false);
    }
  }, [brandPageId, status, q]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(fetchOfferings, 250);
    return () => clearTimeout(t);
  }, [open, fetchOfferings]);

  const existingSet = useMemo(() => new Set(existingOfferingIds), [existingOfferingIds]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selected));
    setSelected(new Set());
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={900}
      title="Add events to this category"
      footer={
        <FooterBar>
          <span style={{ color: "#64748b", fontSize: 13 }}>{selected.size} selected</span>
          <div>
            <Button onClick={onClose} style={{ marginRight: 8 }}>Cancel</Button>
            <Button type="primary" disabled={!selected.size} onClick={handleConfirm}>
              Add {selected.size || ""} event{selected.size === 1 ? "" : "s"}
            </Button>
          </div>
        </FooterBar>
      }
    >
      <Toolbar>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by title…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          allowClear
        />
        <Select
          allowClear
          showSearch
          placeholder="All brand pages"
          value={brandPageId}
          onChange={setBrandPageId}
          optionFilterProp="label"
          options={brandPages.map((b) => ({ value: b.brandPageId, label: b.name || b.slug }))}
        />
        <Select
          value={status}
          onChange={setStatus}
          options={[
            { value: "upcoming", label: "Upcoming" },
            { value: "past", label: "Past" },
            { value: "all", label: "All" },
          ]}
        />
      </Toolbar>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center" }}><Spin /></div>
      ) : items.length === 0 ? (
        <Empty description="No offerings match" />
      ) : (
        <Grid>
          {items.map((it) => {
            const already = existingSet.has(it.offeringId);
            const sel = selected.has(it.offeringId);
            return (
              <Card
                key={it.offeringId}
                $selected={sel}
                disabled={already}
                onClick={() => !already && toggle(it.offeringId)}
                style={already ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              >
                <Cover $src={it.coverImage} />
                <Body>
                  <Title>{it.title}</Title>
                  <Sub>{it.brandName || "—"}</Sub>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <Tag color={it.isUpcoming ? "blue" : "default"}>{it.isUpcoming ? "Upcoming" : "Past"}</Tag>
                    {it.featureId && <Tag>{it.featureId}</Tag>}
                    {already && <Tag color="green">Already added</Tag>}
                  </div>
                </Body>
              </Card>
            );
          })}
        </Grid>
      )}
    </Modal>
  );
}
